<template>
  <Drawer
    v-model:visible="isVisible"
    header="Edit User"
    position="right"
    class="!w-full md:!w-[450px]"
    block-scroll
  >
    <!-- Loading State -->
    <div
      v-if="isLoadingInitialData"
      class="flex items-center justify-center py-20"
    >
      <ProgressSpinner />
    </div>

    <div v-else class="space-y-6">
      <!-- Basic Fields -->
      <UserBasicFields
        v-model:name="formData.name"
        v-model:email="formData.email"
        v-model:phone-number="formData.phone_number"
        :no-email="!hasLogin"
        :name-error="v$.name.$error ? v$.name.$errors[0]?.$message : ''"
        :email-error="v$.email.$error ? v$.email.$errors[0]?.$message : ''"
      />

      <!-- Role Selection -->
      <RoleSelector
        v-model:is-player="formData.isPlayer"
        v-model:is-contact="formData.isContact"
        v-model:is-coach="formData.isCoach"
        :is-contact-disabled="!playerModelEnabled && wasOriginallyContact"
      />

      <!-- Teams Selection (for players) -->
      <div v-if="formData.isPlayer" class="space-y-2">
        <label for="player-teams" class="text-sm font-medium text-gray-700">
          Teams <span v-if="!playerModelEnabled" class="text-red-500">*</span>
        </label>
        <MultiSelect
          id="player-teams"
          v-model="playerTeamIds"
          :options="allAvailableTeams"
          option-label="name"
          option-value="id"
          placeholder="Select teams"
          class="w-full"
          :loading="isLoadingTeams"
          append-to="body"
        />
      </div>

      <!-- Teams Selection (for coaches) -->
      <div v-if="formData.isCoach" class="space-y-2">
        <label for="coach-teams" class="text-sm font-medium text-gray-700"
          >Teams</label
        >
        <MultiSelect
          id="coach-teams"
          v-model="coachTeamIds"
          :options="allAvailableTeams"
          option-label="name"
          option-value="id"
          placeholder="Select teams"
          class="w-full"
          :loading="isLoadingTeams"
          append-to="body"
        />
      </div>

      <!-- Linked Players Selection (for contacts) -->
      <div v-if="formData.isContact" class="space-y-2">
        <label for="linked-players" class="text-sm font-medium text-gray-700">
          Linked Players
        </label>
        <MultiSelect
          id="linked-players"
          v-model="linkedProfileIds"
          :options="profiles"
          option-label="name"
          option-value="id"
          placeholder="Select players to link"
          class="w-full"
          :loading="isLoadingProfiles"
          :disabled="linkedProfilesLoadFailed"
          filter
          append-to="body"
        >
          <template #option="{ option }">
            <div class="flex flex-col">
              <span>{{ option.name }}</span>
              <span class="text-xs text-gray-500">{{ option.email }}</span>
            </div>
          </template>
        </MultiSelect>
        <small v-if="linkedProfilesLoadFailed" class="text-xs text-red-500">
          Couldn't load players — linked players can't be edited right now.
        </small>
        <small v-else class="text-xs text-gray-500">
          Optional — you can link players later.
        </small>
      </div>

      <!-- Admin Toggle -->
      <AdminToggle v-model:is-admin="formData.roles.admin" />
    </div>

    <template #footer>
      <div class="flex justify-end pr-6 pb-4">
        <Button label="Save" :loading="isSubmitting" @click="handleSubmit" />
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref } from "vue";
import Drawer from "primevue/drawer";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import UserBasicFields from "./UserBasicFields.vue";
import RoleSelector from "./RoleSelector.vue";
import AdminToggle from "./AdminToggle.vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useVuelidate } from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import type {
  AdminProfile,
  AdminCoach,
  PatchedAdminProfile,
} from "@/api/model";
import { useUpdateUser } from "../composables/useUpdateUser";
import { useUserTeams } from "../composables/useUserTeams";
import { useCoachManagement } from "../composables/useCoachManagement";
import { addContactToUser } from "../api";
import {
  useOrganizationProfiles,
  type ProfileOption,
} from "../composables/useOrganizationProfiles";
import { ProfileRole } from "../constants/roles";
import { isValidPhoneNumber } from "@/shared/utils/phone-number";
import { useWaffle } from "@/shared/composables/useWaffle";

interface Props {
  readonly profile: AdminProfile;
  readonly orgId: number;
}

const props = defineProps<Props>();

const isVisible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  profileUpdated: [];
}>();

const { isSubmitting, updateUser } = useUpdateUser();

// Under player_model a player doesn't need a team (MAI-4171).
const waffle = useWaffle();
const playerModelEnabled = computed(() => waffle.isActive("player_model"));
// immediate: the drawer is v-if-mounted already open, so a bare visibility
// watch never fires and the flag would read off forever (same trap as MAI-4995).
watch(
  isVisible,
  (open) => {
    if (open) waffle.fetchStatus();
  },
  { immediate: true }
);
const { makeCoach, removeCoach } = useCoachManagement();

const toast = useToast();
const confirm = useConfirm();

// Managed athletes (MAI-4171) have no login: user is null and email is absent.
// Their name/phone can be edited, but there is no email to require or update.
const hasLogin = !!props.profile.user;

const {
  teams,
  allAvailableTeams,
  isLoading: isLoadingTeams,
  loadUserTeams,
  loadAvailableTeams,
  addTeam,
  removeTeam,
} = useUserTeams(props.profile.id, props.profile.organization?.id);

const formData = reactive({
  name: props.profile.name!,
  email: props.profile.email ?? "",
  phone_number: props.profile.phone_number ?? "",
  isPlayer: props.profile.roles?.includes(ProfileRole.player) ?? false,
  isContact: props.profile.roles?.includes(ProfileRole.contact) ?? false,
  isCoach: props.profile.is_coach,
  roles: {
    admin: props.profile.is_admin!,
  },
});

const coachId = ref<number | undefined>(
  props.profile.is_coach && props.profile.coach
    ? (props.profile.coach as AdminCoach).id
    : undefined
);
const coachTeamIds = ref<number[]>([]);
const playerTeamIds = ref<number[]>([]);
const linkedProfileIds = ref<number[]>([]);
// Baseline for change detection: null until the players list loads
// successfully. While null, associated_contact is never sent — the backend
// does profiles.set(...), so a payload built from unloaded state would
// silently unlink every player from this contact.
const initialLinkedProfileIds = ref<number[] | null>(null);
const linkedProfilesLoadFailed = ref(false);

// Track original role states to detect when they're unchecked
const wasOriginallyPlayer =
  props.profile.roles?.includes(ProfileRole.player) ?? false;
const wasOriginallyContact =
  props.profile.roles?.includes(ProfileRole.contact) ?? false;
const wasOriginallyCoach = props.profile.is_coach;

const profiles = ref<ProfileOption[]>([]);
const { isLoading: isLoadingProfiles, fetchProfiles } = useOrganizationProfiles(
  props.orgId
);

// Validation rules (no email rules for managed athletes — they have no login)
const rules = computed(() => ({
  name: {
    required: helpers.withMessage("Name is required", required),
  },
  email: hasLogin
    ? {
        required: helpers.withMessage("Email is required", required),
        email: helpers.withMessage("Must be a valid email", email),
      }
    : {},
}));

const v$ = useVuelidate(rules, formData);

// Track initial data loading
const isLoadingInitialData = ref(true);

// Load initial data
async function loadInitialData() {
  isLoadingInitialData.value = true;

  try {
    // Load teams data
    loadUserTeams(props.profile.teams || []);
    await loadAvailableTeams();

    // Both pickers start from the loaded teams so switching the role
    // (staff <-> player) offers the current roster instead of an empty list.
    playerTeamIds.value = teams.value.map((t) => t.id);
    coachTeamIds.value = teams.value.map((t) => t.id);

    // Load profiles and set linked IDs if profile is a contact
    if (formData.isContact) {
      try {
        profiles.value = await fetchProfiles({ role: ProfileRole.player });
        if (Array.isArray(props.profile.linked_player_profiles)) {
          linkedProfileIds.value = props.profile.linked_player_profiles.map(
            (p: any) => p.id
          );
        }
        initialLinkedProfileIds.value = [...linkedProfileIds.value];
      } catch (error) {
        // Leave initialLinkedProfileIds null so submit never touches links
        linkedProfilesLoadFailed.value = true;
      }
    }
  } finally {
    isLoadingInitialData.value = false;
  }
}

// Initialize on mount
loadInitialData();

// Watch for contact role changes to load profiles when needed
watch(
  () => formData.isContact,
  async (isContact) => {
    if (isContact) {
      try {
        // Always refetch profiles when contact is enabled to get latest player list
        profiles.value = await fetchProfiles({ role: ProfileRole.player });
        linkedProfilesLoadFailed.value = false;
        // Newly-contact profiles start with no links
        initialLinkedProfileIds.value ??= [];
      } catch (error) {
        linkedProfilesLoadFailed.value = true;
      }
    }
  }
);

async function handleSubmit() {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  // Safety check: Player and Coach cannot both be selected
  if (formData.isPlayer && formData.isCoach) {
    toast.add({
      severity: "error",
      summary: "Validation Error",
      detail: "A profile cannot be both a Player and Staff",
      life: 5000,
    });
    return;
  }

  // Validate that player has a team selected (team optional under player_model)
  if (
    formData.isPlayer &&
    playerTeamIds.value.length === 0 &&
    !playerModelEnabled.value
  ) {
    toast.add({
      severity: "error",
      summary: "Validation Error",
      detail: "Players must be assigned to a team",
      life: 5000,
    });
    return;
  }

  // No "at least one role" check: a person created with "Continue without
  // role" has none, and requiring one here would block editing their name or
  // email until someone assigned them a role.

  // Only send associated_contact when the selection loaded successfully AND
  // actually changed — the backend replaces all links with the payload, so an
  // accidental or never-loaded empty selection must never reach it.
  const initialIds = initialLinkedProfileIds.value;
  const linkedIdsChanged =
    formData.isContact &&
    initialIds !== null &&
    !haveSameIds(linkedProfileIds.value, initialIds);

  const updateData: PatchedAdminProfile = {
    name: formData.name.trim(),
    // Managed athletes have no user, so there is no email to update
    ...(hasLogin && { email: formData.email.trim() }),
    phone_number: formData.phone_number.trim(),
    is_admin: formData.roles.admin,
    // Under the player model the toggles drive the roles PATCH directly:
    // the backend adds/removes player (identity + team) and parent (bare
    // contact / safe delete) itself (MAI-5007). Computed roles the backend
    // requires restated: admin (from the toggle) and instructor.
    ...(playerModelEnabled.value && {
      roles: [
        ...(formData.isPlayer ? [ProfileRole.player] : []),
        ...(formData.isContact ? [ProfileRole.contact] : []),
        ...(formData.isCoach ? [ProfileRole.coach] : []),
        ...(formData.roles.admin ? [ProfileRole.admin] : []),
        ...(props.profile.roles?.includes(ProfileRole.instructor)
          ? [ProfileRole.instructor]
          : []),
      ],
    }),
    ...(linkedIdsChanged && {
      associated_contact: { linked_profile_ids: [...linkedProfileIds.value] },
    }),
  };

  // Intentionally clearing all links is allowed, but only with confirmation
  if (linkedIdsChanged && linkedProfileIds.value.length === 0) {
    confirm.require({
      header: "Unlink all players?",
      message: `This will unlink all ${initialIds!.length} players from this contact.`,
      icon: "pi pi-exclamation-triangle",
      accept: () => submitUpdate(updateData),
    });
    return;
  }

  await submitUpdate(updateData);
}

function haveSameIds(a: readonly number[], b: readonly number[]) {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((id, index) => id === sortedB[index]);
}

async function submitUpdate(updateData: PatchedAdminProfile) {
  const success = await updateUser(props.profile.id, updateData);

  if (success) {
    // Handle player role removal - remove all team assignments
    // (flag-on the backend clears the team as part of the roles PATCH)
    if (
      !playerModelEnabled.value &&
      wasOriginallyPlayer &&
      !formData.isPlayer
    ) {
      for (const team of teams.value) {
        await removeTeam(team.id);
      }
    }

    // Handle coach role removal - remove coach status
    // (flag-on the roles PATCH archives the coach server-side)
    if (!playerModelEnabled.value && wasOriginallyCoach && !formData.isCoach) {
      if (!props.profile.organization?.id) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Organization ID is missing",
          life: 3000,
        });
      } else if (!coachId.value) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Staff ID is missing",
          life: 3000,
        });
      } else {
        const coachRemoved = await removeCoach(
          props.profile.organization.id,
          coachId.value
        );

        if (coachRemoved) {
          coachId.value = undefined;
        } else {
          toast.add({
            severity: "error",
            summary: "Warning",
            detail: "Failed to remove staff status",
            life: 5000,
          });
        }
      }
    }

    if (!playerModelEnabled.value && !wasOriginallyCoach && formData.isCoach) {
      if (!props.profile.organization?.id) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Organization ID is missing",
          life: 3000,
        });
      } else {
        const result = await makeCoach(
          props.profile.organization.id,
          props.profile.id
        );

        if (result.success) {
          if (result.coachId) {
            coachId.value = result.coachId;
          }
        } else {
          toast.add({
            severity: "error",
            summary: "Warning",
            detail: "Failed to make profile staff",
            life: 5000,
          });
        }
      }
    }

    const contactLinkingHandledByPatch = !!updateData.associated_contact;
    // Flag-on, a newly toggled parent needs no linked player: the roles PATCH
    // mints a bare parent-first contact server-side (MAI-5007).
    if (
      !playerModelEnabled.value &&
      !wasOriginallyContact &&
      formData.isContact &&
      !contactLinkingHandledByPatch
    ) {
      if (!props.profile.organization?.id) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Organization ID is missing",
          life: 3000,
        });
      } else {
        // For each linked profile, create a contact association
        for (const linkedProfileId of linkedProfileIds.value) {
          const contactResult = await addContactToUser(props.profile.id, {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone_number: formData.phone_number.trim(),
            primary_contact: false,
            organization: props.profile.organization.id,
            profile: linkedProfileId,
          });

          if ("error" in contactResult) {
            toast.add({
              severity: "error",
              summary: "Warning",
              detail: `Failed to link profile ${linkedProfileId}: ${contactResult.error}`,
              life: 5000,
            });
          }
        }
      }
    }

    // Players and staff both hold a set of teams: add the newly
    // picked ones and remove the deselected ones, never replace.
    // A flipped role starts with no teams, so every picked team is new.
    if (formData.isCoach || formData.isPlayer) {
      const roleChanged =
        formData.isCoach !== wasOriginallyCoach ||
        formData.isPlayer !== wasOriginallyPlayer;
      const currentTeamIds = roleChanged ? [] : teams.value.map((t) => t.id);
      const selectedTeamIds = formData.isCoach
        ? coachTeamIds.value
        : playerTeamIds.value;

      const added = selectedTeamIds.filter(
        (id) => !currentTeamIds.includes(id)
      );
      const removed = currentTeamIds.filter(
        (id) => !selectedTeamIds.includes(id)
      );

      // Add new teams
      for (const teamId of added) {
        await addTeam(teamId);
      }

      // Remove teams
      for (const teamId of removed) {
        await removeTeam(teamId);
      }
    }

    emit("profileUpdated");
    isVisible.value = false;
  }
}
</script>
